import { useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { useDndMonitor } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  FaFile,
  FaFilePdf,
  FaFileZipper,
  FaFileAudio,
  FaNoteSticky,
  FaPlay,
  FaDownload,
  FaTrash,
} from "react-icons/fa6";
import { formatUploadedAt, formatUploadedAtParts } from "../../lib/formatUploadedAt";
import { FileExtIcon } from "./FileExtIcon";
import { AudioTile } from "./AudioTile";

const CODE_EXTS = new Set([
  "js", "jsx", "ts", "tsx", "py", "go", "rb", "rs", "java", "kt", "swift",
  "c", "cpp", "h", "hpp", "cs", "php", "sh", "bash", "zsh",
  "css", "scss", "html", "xml", "yaml", "yml", "toml", "ini", "env",
  "md", "mdx", "rst", "log", "sql", "graphql", "proto",
  "json", "csv", "tsv",
]);

const ARCHIVE_EXTS = new Set(["zip", "tar", "gz", "tgz", "bz2", "7z", "rar"]);

// MIME types the browser can actually render in <img> / <video> / <audio>.
// Without this allowlist, oddballs like `image/vnd.adobe.photoshop` (a .psd)
// match `image/*` and render as a broken thumbnail.
const PREVIEWABLE_IMAGE = new Set([
  "image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp",
  "image/avif", "image/svg+xml", "image/bmp", "image/x-icon", "image/heic",
]);
const PREVIEWABLE_VIDEO = new Set([
  "video/mp4", "video/webm", "video/ogg", "video/quicktime",
]);
const PREVIEWABLE_AUDIO = new Set([
  "audio/mpeg", "audio/mp3", "audio/mp4", "audio/m4a", "audio/x-m4a",
  "audio/ogg", "audio/wav", "audio/x-wav", "audio/webm", "audio/aac",
  "audio/flac", "audio/x-flac", "audio/opus",
]);

export function categorize(file) {
  if (file.kind === "note") return "note";
  const mime = (file.mime || "").toLowerCase();
  const name = (file.name || "").toLowerCase();
  const ext = name.split(".").pop();

  if (PREVIEWABLE_IMAGE.has(mime)) return "image";
  if (PREVIEWABLE_VIDEO.has(mime)) return "video";
  if (PREVIEWABLE_AUDIO.has(mime)) return "audio";
  if (mime === "application/pdf" || ext === "pdf") return "pdf";
  if (mime.startsWith("text/") || mime === "application/json" || CODE_EXTS.has(ext)) {
    return "code";
  }
  if (ARCHIVE_EXTS.has(ext)) return "archive";
  return "binary";
}

export function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export function matchesQuery(file, query) {
  if (!query) return false;
  const q = query.toLowerCase();
  return (file.name || "").toLowerCase().includes(q);
}

function Highlight({ text, query }) {
  if (!query) return text;
  const lower = (text || "").toLowerCase();
  const q = query.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx < 0) return text;
  const before = text.slice(0, idx);
  const hit = text.slice(idx, idx + q.length);
  const after = text.slice(idx + q.length);
  return (
    <>
      {before}
      <mark className="bg-amber-300/60 dark:bg-amber-300/40 text-inherit rounded px-0.5">
        {hit}
      </mark>
      <Highlight text={after} query={query} />
    </>
  );
}

function fileExt(file) {
  const name = (file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";
  return ext && /^[a-z0-9]+$/.test(ext) ? ext : "";
}

function getDisplayType(file, kind) {
  if (kind === "note") return "note";
  const ext = fileExt(file);
  if (ext && ext.length <= 6) return ext.toUpperCase();
  const sub = (file.mime || "").split("/").pop();
  if (!sub || sub === "octet-stream") return "FILE";
  return sub.toUpperCase();
}

const MEDIA_BASELINE = 220;

function mediaWidth(file) {
  const w = file.width;
  const h = file.height;
  if (!w || !h) return MEDIA_BASELINE;
  if (h > w) return Math.max(100, Math.round(MEDIA_BASELINE * (w / h)));
  return MEDIA_BASELINE;
}

export function FileTile({ file, onOpen, onDownload, onDelete, mediaUrl, canDelete, tileRef, position, expandedWidth, ready, searchQuery = "", searchFlashTick = 0 }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: file.id });
  const innerRef = useRef(null);
  const draggedRef = useRef(false);

  useDndMonitor({
    onDragStart(event) {
      if (event.active.id === file.id) draggedRef.current = true;
    },
    onDragEnd() {
      setTimeout(() => { draggedRef.current = false; }, 0);
    },
  });

  const kind = categorize(file);
  const isMedia = kind === "image" || kind === "video";

  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;
  const isMatch = isSearching && matchesQuery(file, trimmedQuery);
  const isDimmed = isSearching && !isMatch;

  const widthStyle = isMedia
    ? { width: mediaWidth(file) }
    : expandedWidth != null
      ? { width: expandedWidth }
      : {};

  const widthClass =
    isMedia || expandedWidth != null ? "" : "w-fit min-w-[140px] max-w-[280px]";

  const style = {
    ...widthStyle,
    position: "absolute",
    left: position?.x ?? 0,
    top: position?.y ?? 0,
    visibility: ready ? "visible" : "hidden",
    transform: CSS.Transform.toString(transform),
    transition: transition
      ? `${transition}, left 200ms ease, top 200ms ease, opacity 200ms ease`
      : "left 200ms ease, top 200ms ease, opacity 200ms ease",
    opacity: isDragging ? 0.4 : isDimmed ? 0.3 : 1,
    zIndex: isDragging ? 50 : "auto",
  };

  function setRefs(el) {
    setNodeRef(el);
    innerRef.current = el;
    if (typeof tileRef === "function") tileRef(el);
  }

  function handleOpen() {
    if (draggedRef.current) return;
    onOpen(file);
  }

  const matchClass = isMatch
    ? "ring-2 ring-amber-400/70 shadow-[0_0_20px_-4px_rgba(251,191,36,0.6)]"
    : "border border-zinc-200/70 dark:border-zinc-700/60 hover:border-zinc-300 dark:hover:border-zinc-600";

  return (
    <div
      ref={setRefs}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleOpen}
      className={`${widthClass} relative group cursor-grab active:cursor-grabbing rounded-2xl bg-white/95 dark:bg-zinc-900/80 backdrop-blur-sm ${matchClass} hover:shadow-xl hover:shadow-zinc-900/5 dark:hover:shadow-black/20 overflow-hidden focus:outline-none focus:ring-2 focus:ring-zinc-300 dark:focus:ring-zinc-600`}
    >
      <TileBody file={file} kind={kind} mediaUrl={mediaUrl} searchQuery={trimmedQuery} />

      {isMatch && searchFlashTick > 0 && (
        <span
          key={`fire-${searchFlashTick}`}
          className="fire-border pointer-events-none absolute -inset-px rounded-2xl"
          aria-hidden
        />
      )}

      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        {canDelete && (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onDelete(file); }}
            className="w-6 h-6 rounded-full bg-white/90 dark:bg-zinc-900/80 backdrop-blur border border-zinc-200 dark:border-zinc-700 flex items-center justify-center text-red-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 dark:hover:bg-red-950/40 dark:hover:border-red-900 cursor-pointer"
            aria-label="Eliminar"
            title="Eliminar"
          >
            <FaTrash className="w-3 h-3" />
          </button>
        )}
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDownload(file); }}
          className="w-6 h-6 rounded-full bg-white/90 dark:bg-zinc-900/80 backdrop-blur border border-zinc-200 dark:border-zinc-700 flex items-center justify-center text-zinc-400 dark:text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-100 cursor-pointer"
          aria-label="Download"
          title="Download"
        >
          <FaDownload className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function FileUploadedAt({ file, variant = "card" }) {
  const parts = formatUploadedAtParts(file.uploadedAt);
  if (!parts) return null;

  const title = formatUploadedAt(file.uploadedAt);
  const line = (
    <>
      <span className="opacity-60 select-none" aria-hidden>↑</span>{" "}
      {parts.date}
      <span className="opacity-50"> · </span>
      {parts.time}
    </>
  );

  if (variant === "media") {
    return (
      <p
        className="absolute bottom-0 inset-x-0 z-[1] px-1.5 pb-1 pt-3 bg-gradient-to-t from-black/50 to-transparent text-[7px] leading-tight text-white/50 tabular-nums tracking-wide pointer-events-none"
        title={title}
      >
        {line}
      </p>
    );
  }

  return (
    <p
      className="mt-1 text-[7px] leading-tight text-zinc-500/70 dark:text-zinc-500/60 tabular-nums tracking-wide"
      title={title}
    >
      {line}
    </p>
  );
}

function TileBody({ file, kind, mediaUrl, searchQuery }) {
  if (kind === "image" && mediaUrl) {
    return (
      <div
        className="relative w-full bg-zinc-100 dark:bg-zinc-800"
        style={{
          aspectRatio:
            file.width && file.height ? `${file.width} / ${file.height}` : "1 / 1",
        }}
      >
        <img
          src={mediaUrl}
          alt={file.name}
          className="w-full h-full object-cover pointer-events-none"
          draggable="false"
          loading="lazy"
        />
        <FileUploadedAt file={file} variant="media" />
      </div>
    );
  }

  if (kind === "video" && mediaUrl) {
    return (
      <div
        className="relative w-full bg-zinc-900"
        style={{
          aspectRatio:
            file.width && file.height ? `${file.width} / ${file.height}` : "16 / 9",
        }}
      >
        <video
          src={mediaUrl}
          className="w-full h-full object-cover pointer-events-none"
          muted
          playsInline
          preload="metadata"
        />
        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center text-zinc-900">
            <FaPlay className="w-4 h-4 ml-0.5" />
          </span>
        </span>
        <FileUploadedAt file={file} variant="media" />
      </div>
    );
  }

  if (kind === "image" || kind === "video") {
    return (
      <div
        className="relative w-full bg-zinc-100 dark:bg-zinc-800 animate-pulse"
        style={{
          aspectRatio:
            file.width && file.height ? `${file.width} / ${file.height}` : "1 / 1",
        }}
      >
        <FileUploadedAt file={file} variant="media" />
      </div>
    );
  }

  if (kind === "audio" && mediaUrl) {
    return <AudioTile file={file} mediaUrl={mediaUrl} />;
  }

  return <GenericTile file={file} kind={kind} searchQuery={searchQuery} />;
}

function GenericTile({ file, kind, searchQuery }) {
  const ext = fileExt(file);
  const useExtIcon = (kind === "code" || kind === "binary") && ext.length > 0 && ext.length <= 4;

  const FixedIcon =
    kind === "note"    ? FaNoteSticky :
    kind === "pdf"     ? FaFilePdf :
    kind === "archive" ? FaFileZipper :
    kind === "audio"   ? FaFileAudio :
                         FaFile;

  const fixedIconColor =
    kind === "note"    ? "text-amber-400" :
    kind === "pdf"     ? "text-red-400" :
    kind === "archive" ? "text-amber-400" :
    kind === "audio"   ? "text-emerald-400" :
                         "text-zinc-400";

  return (
    <div className="p-4 flex flex-col gap-1.5 pointer-events-none">
      <div className="flex items-center gap-2.5 min-w-0">
        {useExtIcon ? (
          <FileExtIcon ext={ext} className="w-8 h-8 shrink-0" />
        ) : (
          <FixedIcon className={`w-8 h-8 shrink-0 ${fixedIconColor}`} />
        )}
        <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wide leading-tight">
          {formatBytes(file.size)} · {getDisplayType(file, kind)}
        </p>
      </div>
      <p className="text-xs font-semibold text-zinc-800 dark:text-zinc-100 break-words leading-snug">
        <Highlight text={file.name} query={searchQuery} />
      </p>
      {kind === "note" && file.content && (
        <p className="text-[8px] text-zinc-500 dark:text-zinc-400 line-clamp-2 leading-snug whitespace-pre-wrap">
          {file.content}
        </p>
      )}
      <FileUploadedAt file={file} />
    </div>
  );
}
